import { useState } from 'react';
import { PRESENTER_SLIDE_NAMES } from './PresentationBannerConstants';

interface RearrangeSlide { id: number; name: string; }

interface Props { onSignUp: () => void; }

const SLIDE_ICONS = [
  'ri-home-4-line', 'ri-list-ordered', 'ri-file-list-2-line', 'ri-bar-chart-box-line',
  'ri-error-warning-line', 'ri-lightbulb-line', 'ri-database-2-line', 'ri-briefcase-4-line',
  'ri-roadmap-line', 'ri-group-line', 'ri-line-chart-line', 'ri-arrow-right-circle-line',
];

const SLIDE_COLORS = [
  '#6366f1', '#f43f5e', '#10b981', '#f59e0b', '#0ea5e9', '#8b5cf6',
  '#ec4899', '#14b8a6', '#84cc16', '#f97316', '#a855f7', '#10b981',
];

const DEFAULT_ORDER: RearrangeSlide[] = PRESENTER_SLIDE_NAMES.map((name, i) => ({ id: i, name }));

export function SlideRearrangePanel({ onSignUp }: Props) {
  const [slides, setSlides]       = useState<RearrangeSlide[]>(DEFAULT_ORDER);
  const [draggingId, setDraggingId]   = useState<number | null>(null);
  const [dragOverId, setDragOverId]   = useState<number | null>(null);
  const [saved, setSaved]         = useState(false);

  const isReordered  = slides.some((s, i) => s.id !== i);
  const movedCount   = slides.filter((s, i) => s.id !== i).length;

  const handleDragStart = (id: number) => setDraggingId(id);

  const handleDragOver  = (e: React.DragEvent, id: number) => {
    e.preventDefault();
    if (id !== draggingId) setDragOverId(id);
  };

  const handleDrop = (targetId: number) => {
    if (draggingId === null || draggingId === targetId) return;
    setSlides(prev => {
      const arr     = [...prev];
      const fromIdx = arr.findIndex(s => s.id === draggingId);
      const toIdx   = arr.findIndex(s => s.id === targetId);
      if (fromIdx === -1 || toIdx === -1) return prev;
      const [item] = arr.splice(fromIdx, 1);
      arr.splice(toIdx, 0, item);
      return arr;
    });
    setDraggingId(null);
    setDragOverId(null);
    setSaved(false);
  };

  const handleDragEnd = () => { setDraggingId(null); setDragOverId(null); };

  const handleReset = () => { setSlides(DEFAULT_ORDER); setSaved(false); };

  const handleSave = () => { setSaved(true); setTimeout(() => setSaved(false), 2200); };

  return (
    <div className="border border-white/12 rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-white/5">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-violet-500/15">
            <i className="ri-drag-drop-line text-violet-400 text-sm"></i>
          </div>
          <div>
            <p className="text-xs font-bold text-white">Slide Order</p>
            <p className="text-xs text-slate-500">Drag cards to reorder · saved order applies on export</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isReordered && (
            <button onClick={handleReset} className="flex items-center gap-1 px-2.5 py-1.5 bg-white/8 border border-white/12 text-slate-400 text-xs font-semibold rounded-lg cursor-pointer hover:text-white hover:bg-white/14 transition-all whitespace-nowrap">
              <i className="ri-restart-line text-xs"></i>Reset
            </button>
          )}
          <button onClick={saved ? undefined : handleSave} className={`flex items-center gap-1 px-2.5 py-1.5 text-xs font-bold rounded-lg cursor-pointer transition-all whitespace-nowrap ${saved ? 'bg-emerald-500/20 border border-emerald-500/30 text-emerald-300' : 'bg-violet-500/20 border border-violet-500/30 text-violet-300 hover:bg-violet-500/30'}`}>
            <i className={`text-xs ${saved ? 'ri-check-line' : 'ri-save-line'}`}></i>
            {saved ? 'Saved!' : 'Save Order'}
          </button>
        </div>
      </div>

      {/* Drag-and-drop grid */}
      <div className="p-4 grid grid-cols-4 gap-2.5">
        {slides.map((slide, position) => {
          const origIdx   = slide.id;
          const isDragging  = draggingId === slide.id;
          const isDragOver  = dragOverId  === slide.id;
          const color     = SLIDE_COLORS[origIdx];
          const icon      = SLIDE_ICONS[origIdx];

          return (
            <div
              key={slide.id}
              draggable
              onDragStart={() => handleDragStart(slide.id)}
              onDragOver={e => handleDragOver(e, slide.id)}
              onDrop={() => handleDrop(slide.id)}
              onDragEnd={handleDragEnd}
              className={`relative rounded-xl border-2 p-2.5 transition-all duration-150 select-none ${
                isDragging  ? 'opacity-35 scale-95 border-white/30 bg-white/5 cursor-grabbing'
                : isDragOver ? 'border-violet-400/70 bg-violet-500/12 scale-[1.04] cursor-grabbing'
                : 'border-white/10 bg-white/5 hover:border-white/25 hover:bg-white/8 cursor-grab'
              }`}
            >
              {/* Position badge */}
              <div className="absolute -top-1.5 -left-1.5 w-5 h-5 rounded-full bg-slate-800 border border-white/15 flex items-center justify-center z-10">
                <span className="text-xs font-extrabold text-slate-400 leading-none">{position + 1}</span>
              </div>

              {/* Drag handle icon */}
              <div className="absolute top-1.5 right-1.5">
                <i className="ri-drag-move-2-fill text-slate-600 text-xs"></i>
              </div>

              {/* Slide type icon */}
              <div className="w-8 h-8 rounded-lg flex items-center justify-center mb-2 mx-auto" style={{ backgroundColor: color + '20', border: `1px solid ${color}35` }}>
                <i className={`${icon} text-sm`} style={{ color }}></i>
              </div>

              {/* Name */}
              <p className="text-center font-semibold text-slate-300 leading-tight line-clamp-2" style={{ fontSize: '10px' }}>
                {slide.name}
              </p>

              {/* Moved indicator dot */}
              {position !== origIdx && (
                <div className="absolute bottom-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-violet-400"></div>
              )}

              {/* Drop-target highlight ring */}
              {isDragOver && (
                <div className="absolute inset-0 rounded-xl border-2 border-violet-400/60 pointer-events-none"></div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-4 py-2.5 border-t border-white/10 bg-white/3">
        <p className="text-xs text-slate-500">
          {isReordered
            ? `${movedCount} slide${movedCount > 1 ? 's' : ''} moved from original position`
            : 'Default order — drag any card to reorder'}
        </p>
        <button onClick={onSignUp} className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-500/20 border border-violet-500/30 text-violet-300 text-xs font-bold rounded-lg cursor-pointer hover:bg-violet-500/30 transition-all whitespace-nowrap">
          <i className="ri-export-line text-xs"></i>Export with Order
        </button>
      </div>
    </div>
  );
}
